import type { RestTimerState, WorkoutSession } from '../../db/types';
import { normalizeRestDuration } from '../../shared/utils/number';

export function restTimerState(session: WorkoutSession, now = Date.now()): RestTimerState {
  if (session.restEndsAt) {
    const remainingSeconds = Math.max(0, Math.ceil((Date.parse(session.restEndsAt) - now) / 1000));
    return {
      selectedSeconds: session.restSelectedSeconds,
      remainingSeconds,
      isRunning: remainingSeconds > 0,
      isPaused: false,
      isComplete: remainingSeconds === 0
    };
  }
  if (session.restPausedSeconds !== null) {
    return {
      selectedSeconds: session.restSelectedSeconds,
      remainingSeconds: session.restPausedSeconds,
      isRunning: false,
      isPaused: true,
      isComplete: session.restPausedSeconds === 0
    };
  }
  return {
    selectedSeconds: session.restSelectedSeconds,
    remainingSeconds: session.restSelectedSeconds,
    isRunning: false,
    isPaused: false,
    isComplete: false
  };
}

export function startedRestPatch(seconds: number, now = Date.now()): Pick<WorkoutSession, 'restSelectedSeconds' | 'restEndsAt' | 'restPausedSeconds'> {
  const normalized = normalizeRestDuration(seconds);
  return { restSelectedSeconds: normalized, restEndsAt: new Date(now + normalized * 1000).toISOString(), restPausedSeconds: null };
}

export function pausedRestPatch(session: WorkoutSession, now = Date.now()): Pick<WorkoutSession, 'restEndsAt' | 'restPausedSeconds'> {
  const state = restTimerState(session, now);
  return { restEndsAt: null, restPausedSeconds: state.remainingSeconds };
}

export function resumedRestPatch(session: WorkoutSession, now = Date.now()): Pick<WorkoutSession, 'restEndsAt' | 'restPausedSeconds'> {
  const seconds = session.restPausedSeconds ?? session.restSelectedSeconds;
  return { restEndsAt: new Date(now + seconds * 1000).toISOString(), restPausedSeconds: null };
}

export function adjustedRestPatch(session: WorkoutSession, delta: number, now = Date.now()): Pick<WorkoutSession, 'restSelectedSeconds' | 'restEndsAt' | 'restPausedSeconds'> {
  const state = restTimerState(session, now);
  const selectedSeconds = normalizeRestDuration(session.restSelectedSeconds + delta);
  const appliedDelta = selectedSeconds - session.restSelectedSeconds;
  const remainingSeconds = Math.min(900, Math.max(0, state.remainingSeconds + appliedDelta));
  if (state.isRunning) {
    return {
      restSelectedSeconds: selectedSeconds,
      restEndsAt: new Date(now + remainingSeconds * 1000).toISOString(),
      restPausedSeconds: null
    };
  }
  return {
    restSelectedSeconds: selectedSeconds,
    restEndsAt: null,
    restPausedSeconds: state.isPaused ? remainingSeconds : null
  };
}
